import {
  IAbstractLine,
  ILuckinsItem,
  IPricePotential,
  IProduct,
  IPurchaseOrderLine,
  ISalesCreditLine,
  ISalesInvoice,
  ISalesInvoiceLine,
  ISalesOrder,
  ISalesOrderLine,
  IWarehouse,
  Maybe,
  PriceOrigin,
  ProductClass,
} from '../../graphql/schema/p4types';
import { ICommit } from '../../@types/editableDataGridTypes';
import { useCompany, useSalesOrderConfig } from '../../utils/FetchConfig';
import { useCallback } from 'react';
import { format } from 'date-fns';
import BigNumber from 'bignumber.js';
import { cloneDeep } from '@apollo/client/utilities';
import { IRowUpdatedFunction } from '../EditableDataGrid/EditableDataGrid';

export const grossPriceOptionCreator = ({
  label,
  labelKey,
  valueKey,
}: {
  label: string;
  labelKey: string;
  valueKey: string;
}) => {
  const option: { [key: string]: any; className: string; manualEntry: boolean } = {} as any;
  option[valueKey] = label;
  option[labelKey] = label;
  option.className = 'Select-create-option-placeholder';
  option.manualEntry = true;
  return option;
};

export const grossPriceCreatePrompt = (label: string) => {
  return `Use Manual Price ${label}`;
};

export const getSellingPricePrecision = (rowData: any) => {
  if (rowData && rowData.sellingPricePrecision) {
    return rowData.sellingPricePrecision;
  }
  return 2;
};

export const getQuantityPrecision = (rowData: any) => {
  if (rowData && rowData.quantityPrecision) {
    return rowData.quantityPrecision;
  }
  return 2;
};

export const useGetCostPricePrecision = () => {
  const company = useCompany();

  return useCallback(
    (rowData = null) => {
      return rowData?.costPricePrecision ?? company?.costPricePrecision ?? 2;
    },
    [company?.costPricePrecision]
  );
};

export const normalizeOrderLines = (id: unknown, value: any) => {
  if (value && value.updated && value.updated.idValue) {
    delete value.updated.idValue.className;
    delete value.updated.idValue.manualEntry;
  }
  return value;
};

const isProduct = (product: IProduct | ILuckinsItem): product is IProduct => {
  return (product as IProduct).description !== undefined;
};

const getPrecision = (num: number) => {
  let e = 1;
  while (Math.round(num * e) / e !== num) e *= 10;
  return Math.log(e) / Math.LN10;
};

export const roundByPrecision = (num: number | string, precision: number) => {
  if (!num || precision === null || precision === undefined) {
    return num;
  }

  const isString = typeof num === 'string';
  const numberValue = isString ? Number(num) : num;

  if (isNaN(numberValue)) {
    return num;
  }

  const rounded = numberValue.toFixed(precision);
  return isString ? String(rounded) : rounded;
};

//A wrapper to resolve compatability issues with IRowUpdatedFunction (Different signatures in some places).
//I don't think we use the 'dataid' pass-through anywhere so this can likely be deprecated, but some validation needed to check that assertion.
export const entryGridWithRowUpdatedAndDataId: IRowUpdatedFunction = (commit, dataid, additional) => {
  entryGridWithRowUpdated(commit, additional);
};

type IAbstractLinePartials = Partial<ISalesOrderLine> & Partial<ISalesInvoiceLine> & Partial<IPurchaseOrderLine>;

//Moved to shared location since identical between Sales/PurchaseFunctions.
//Important - Do not directly pass to EditableDataGrid - Use above wrapper or the parameters will be misaligned/incorrect.
export const entryGridWithRowUpdated = <AbstractLine extends IAbstractLine>(
  commit: ICommit<AbstractLine & IAbstractLinePartials>,
  { connectedForm: { values } = {} }: { connectedForm?: { values?: any } } = {}
) => {
  if (!commit.cellKeys) commit.cellKeys = [];
  if (!commit.updated) commit.updated = {} as AbstractLine & { warehouse: IWarehouse };

  if (!commit.original?.createdAt && !commit.original?.initialCreatedAt) {
    commit.updated.initialCreatedAt = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
    commit.cellKeys.push('initialCreatedAt');
  }

  //If user attempts to enter a number with a higher precision than configured, manipulate to a valid precision.
  const precision = commit.original?.quantityPrecision ?? commit.original?.product?.precision ?? 0;
  if (commit.cellKey === 'quantity' && !isNaN(commit.updated.quantity as number)) {
    const value = parseFloat(commit.updated.quantity as string);
    if (!isNaN(value)) {
      const enteredPrecision = getPrecision(value);
      if (enteredPrecision > precision) {
        commit.updated.quantity = value.toFixed(precision);
      }
    }
  }

  if (commit.cellKey === 'product') {
    if (commit.original) {
      Object.keys(commit.original).forEach((key) => {
        if (
          ['product', 'id', 'uuid', 'concurrencyVersion', 'initialCreatedAt', 'createdAt', 'createdBy'].includes(key)
        ) {
          //Don't wipe out the core line fields (May cause merge issues).
          return;
        }
        //commit.updated only contains explicitly set fields.
        //As such, we can wipe out all but the newly-updated fields by simply pushing a cellKey for each value.
        commit.cellKeys.push(key);
      });
    }

    const productReadOnly = commit.updated.product as IProduct | ILuckinsItem;
    const product = cloneDeep(productReadOnly);
    if (product && !isProduct(product)) {
      delete product.shortDescription;
      delete product.otherDescription;
      commit.updated.product = product as IProduct;
    } else if (product) {
      commit.cellKeys.push('description');
      //Mimic services 'defaultDescription' - Avoid 'flicker' on selection.
      commit.updated.description =
        product.salesDescription && product.salesDescription?.length > 0
          ? product.salesDescription
          : product.description;
      //Default the warehouse directly too - Avoids needing to fetch itemInformation any extra time.
      if (!commit.updated.warehouse && values?.warehouse) {
        commit.cellKeys.push('warehouse');
        commit.updated.warehouse = values?.warehouse;
      }
    }
  }

  if (commit.cellKey === 'discountPercentage') {
    // Set which values are to be changed
    commit.cellKeys = ['termA', 'termB', 'termC', 'termD', 'termE'];
    // Change values (typing into discount field sets term A and resets other terms to 0)
    commit.updated.termA = commit.updated.discountPercentage;
    commit.updated.termB = 0;
    commit.updated.termC = 0;
    commit.updated.termD = 0;
    commit.updated.termE = 0;
    if (commit.original?.priceOrigin !== PriceOrigin.Manual) {
      commit.cellKeys.push('priceOriginDescription', 'priceOrigin');
      commit.updated.priceOriginDescription = 'Manual Discount';
      commit.updated.priceOrigin = PriceOrigin.ManualDiscount;
    }
  }

  if (commit.cellKey === 'grossSellingPrice') {
    commit.cellKeys.push('priceOriginDescription', 'priceOrigin', 'termA', 'termB', 'termC', 'termD', 'termE');

    // At this point, grossSellingPrice is either a selected object or a manually entered value
    const selectedObject = commit.updated[commit.cellKey] as number | (IPricePotential & { manualEntry: boolean });

    if (selectedObject) {
      // If the user enters a value and presses tab, the selected option is not wrapped in an object
      if (typeof selectedObject !== 'object') {
        commit.updated.grossSellingPrice = selectedObject;
        setUpdatedManualPricing(commit.updated);
      } else {
        const pricePotential: IPricePotential & { manualEntry: boolean } = selectedObject as IPricePotential & {
          manualEntry: boolean;
        };
        commit.updated.grossSellingPrice = pricePotential.grossPrice;
        if (pricePotential.manualEntry) {
          setUpdatedManualPricing(commit.updated);
        } else {
          commit.updated.priceOriginDescription = pricePotential.description;
          commit.updated.priceOrigin = pricePotential.priceOrigin;
          commit.updated.termA = pricePotential.termA ?? 0;
          commit.updated.termB = pricePotential.termB ?? 0;
          commit.updated.termC = pricePotential.termC ?? 0;
          commit.updated.termD = pricePotential.termD ?? 0;
          commit.updated.termE = pricePotential.termE ?? 0;

          commit.cellKeys.push('termA', 'termB', 'termC', 'termD', 'termE', 'selectedPriceBand');
          commit.updated.selectedPriceBand = pricePotential.priceBand;
          if (commit.original?.__typename === 'SalesOrderLine') {
            commit.updated.manuallySelectedPrice = true;
            commit.updated.priceSourceUpdatedAt = pricePotential?.priceSourceUpdatedAt;
            commit.cellKeys.push('priceSourceUpdatedAt', 'manuallySelectedPrice');
          }
        }
      }
    } else {
      commit.updated.grossSellingPrice = null;
      setUpdatedManualPricing(commit.updated);
    }
  }

  if (
    commit.cellKey === 'activeSellingUnit' &&
    commit.original?.activeSellingUnit?.description !== commit.updated.activeSellingUnit?.description
  ) {
    commit.cellKeys.push('potentialPrices');
    commit.updated.potentialPrices = [];
  }

  if (!commit.cellKeys) commit.cellKeys = [commit.cellKey];

  if (commit.cellKey === 'product' && commit.updated?.product?.productClass === ProductClass.FreeType) {
    commit.updated!.quantity = 1;
    commit.cellKeys.push('quantity');
  }

  commit.cellKeys.push('updated');
  commit.updated.updated = true; //Tag the line as updated so the services know to process it.
};

const setUpdatedManualPricing = (updated: Partial<IAbstractLine>) => {
  updated.priceOriginDescription = 'Manual';
  updated.priceOrigin = PriceOrigin.Manual;
  updated.termA = 0;
  updated.termB = 0;
  updated.termC = 0;
  updated.termD = 0;
  updated.termE = 0;
};

//Credit variant on entryGridWithRowUpdated - ignores changes if the selling price cell is blurred without changing
export const useSalesCreditGridWithRowUpdated = (): IRowUpdatedFunction => {
  return useCallback((commit: ICommit<ISalesCreditLine>, dataid, additional) => {
    if (
      commit.cellKey === 'grossSellingPrice' &&
      (!commit.updated?.grossSellingPrice ||
        commit.original?.grossSellingPrice === parseFloat(commit.updated.grossSellingPrice))
    ) {
      return;
    }

    entryGridWithRowUpdated(commit as ICommit<any>, additional);
  }, []);
};

//Sales variant on entryGridWithRowUpdated - Sets the quantity minimum appropriately (Similar to Purchase variant in PurchaseOrderForm.tsx, just data is from a different source.
//Note - Would not expect 'sales credit' to use this at this time, since a credit is somewhat different for quantities involved.
export const useSalesEntryGridWithRowUpdated = (): IRowUpdatedFunction<ISalesOrderLine | ISalesInvoiceLine> => {
  const orderConfig = useSalesOrderConfig();

  return useCallback<IRowUpdatedFunction<ISalesOrderLine | ISalesInvoiceLine>>(
    (
      commit,
      dataid,
      { connectedForm: { values } = {} }: { connectedForm?: { values?: ISalesOrder | ISalesInvoice } } = {}
    ) => {
      //Call common withRowUpdated first.
      entryGridWithRowUpdated<ISalesOrderLine | ISalesInvoiceLine>(commit as ICommit<any>, {
        connectedForm: { values },
      });

      if (commit.cellKey === 'product' && commit.updated?.product) {
        const product = commit.updated.product as IProduct;
        //Then set the order quantity from the product supplier details record.
        if (orderConfig?.defaultLineQuantity) {
          if (!commit.cellKeys) commit.cellKeys = ['product'];
          commit.cellKeys.push('quantity');

          (commit.updated as ISalesOrderLine).quantity = minimumQuantityToUse(
            product?.orderQuantityMinimum,
            product?.orderQuantityIncrement
          );
        }
      } else if (commit.cellKey === 'displayProfitMargin') {
        // We can't 'change' the cellKey because then ConnectedField won't find the column to get the 'temporary save' meta trigger.
        // Passing the display field as well should be harmless though.
        commit.cellKeys.push('forceProfitMargin');
        commit.updated!.forceProfitMargin = commit.updated?.displayProfitMargin;
      } else if (commit.cellKey === 'displayProfitValue') {
        // We can't 'change' the cellKey because then ConnectedField won't find the column to get the 'temporary save' meta trigger.
        // Passing the display field as well should be harmless though.
        commit.cellKeys.push('forceProfitValue');
        commit.updated!.forceProfitValue = commit.updated?.displayProfitValue;
      } else if (commit.cellKey === 'nettSellingPrice') {
        commit.cellKeys.push('forceNettPrice');
        commit.updated!.forceNettPrice = commit.updated?.nettSellingPrice;
      }
    },
    [orderConfig?.defaultLineQuantity]
  );
};

export const minimumQuantityToUse = (
  orderQuantityMinimum: Maybe<number>,
  orderQuantityIncrement: Maybe<number>
): number => {
  BigNumber.config({ DECIMAL_PLACES: 4 });
  const minimum: BigNumber = new BigNumber(orderQuantityMinimum ?? orderQuantityIncrement ?? 1);
  const increment: BigNumber = new BigNumber(orderQuantityIncrement ?? 1);
  const zero: BigNumber = new BigNumber(0);
  let minimumToUse: BigNumber;

  if (increment.isGreaterThanOrEqualTo(minimum)) {
    minimumToUse = increment;
  } else if (increment.isGreaterThan(zero)) {
    const remainder: BigNumber = minimum.mod(increment);
    minimumToUse = remainder.isGreaterThan(zero) ? minimum.minus(remainder).plus(increment) : minimum;
  } else {
    minimumToUse = minimum;
  }

  return minimumToUse.toNumber();
};
